import {capitalize} from 'lodash'
import {type HgArticleAboutAuthorProps} from '../HgArticleNotes/_components/HgArticleAboutAuthor'
import {HgArticleShareLinkContent} from '../HgArticleNotes/_components/HgArticleShareLinks'
import {Fragment} from 'react'
import {convertToDateString} from '~/design-system/utils'
import {HgTextLink} from '~/design-system/hg/components'

type HgCreditAuthorProps = Pick<
  HgArticleAboutAuthorProps,
  'name' | 'detailPageLink' | 'bioShort'
>

type AssetCreditProps = {
  collaborators: HgCreditAuthorProps[]
  assetType: string
}

export type HgArticleCreditsProps = {
  authors: HgCreditAuthorProps[]
  assetCredits?: AssetCreditProps[]
  datePublished: string | number
  dateUpdated?: string
  articleTitle: string
}

function isAssetCredit(
  props: HgArticleCreditsProps['authors'] | AssetCreditProps
): props is AssetCreditProps {
  return 'assetType' in props
}

function renderCredits(props: HgArticleCreditsProps['authors'] | AssetCreditProps) {
  if (!props) return null
  const creditAuthors = isAssetCredit(props) ? props.collaborators : props
  return (
    <div className="grid gap-s1">
      <div>
        <span>
          {isAssetCredit(props) ? `${capitalize(props.assetType)}:` : 'By: '}
        </span>
        {creditAuthors?.map((author, idx) => (
          <Fragment key={idx}>
            {creditAuthors.length > 1 && idx === creditAuthors.length - 1
              ? ' and '
              : ' '}
            {author.detailPageLink ? (
              <HgTextLink
                variant="underline"
                className="arcadia-ui-2"
                {...author.detailPageLink}
              >
                {author.name}
              </HgTextLink>
            ) : (
              <span>{author.name}</span>
            )}
            {idx < creditAuthors.length - 1 && creditAuthors.length > 2 && ','}
          </Fragment>
        ))}
      </div>
      {creditAuthors?.length === 1 && creditAuthors[0].bioShort && (
        <p className="text-text-subdued">{creditAuthors[0].bioShort}</p>
      )}
    </div>
  )
}

export default function HgArticleCredits({
  authors,
  datePublished,
  dateUpdated,
  assetCredits,
  articleTitle,
}: HgArticleCreditsProps) {
  return (
    <div className="z-[1] col-span-8 col-start-1 mb-s5 grid h-fit grid-cols-subgrid md:col-start-3 lg:col-span-4 lg:col-start-1 lg:row-start-1">
      <div className="col-span-full grid gap-s4">
        <div className="grid gap-s3 text-text-default arcadia-body-4">
          {renderCredits(authors)}
          {assetCredits?.map((credit, idx) => (
            <Fragment key={idx}>{renderCredits(credit)}</Fragment>
          ))}
          <div className="grid gap-s1">
            <span>{convertToDateString(datePublished)}</span>
            {dateUpdated && (
              <span className="text-text-subdued">
                Updated: {convertToDateString(dateUpdated)}
              </span>
            )}
          </div>
        </div>
        <HgArticleShareLinkContent title={articleTitle} />
      </div>
    </div>
  )
}
